/**
 * Sorting and filtering for the Employees table.
 *
 * Pure on purpose: there is no frontend test runner in this project, so the rules that
 * are easy to get quietly wrong live here and are pinned by
 * `employeeSort.checks.mjs` (`node frontend/src/lib/employeeSort.checks.mjs`), the same
 * pattern as `leaveUnits.checks.mjs`.
 *
 * THE THREE THINGS A NAIVE SORT GETS WRONG HERE
 * ---------------------------------------------
 * 1. BALANCES ARE NOT NUMBERS ON THE SCREEN. A row can read "18.5 days" or "42h 30m"
 *    depending on the employee's own display unit, so sorting the rendered text puts a
 *    part-timer with 42 hours above everyone as though he had 42 days. We compare
 *    MINUTES when both rows have them and fall back to days when they don't — the same
 *    rule the booking balance check in routers/leave.py uses, deliberately, so the two
 *    can't drift. Note days and minutes legitimately disagree for uneven workers (days
 *    count sessions, minutes count cost — see the ledger-flip note in the handover), so
 *    "just convert days to minutes" is not available to us.
 *
 * 2. DATES MUST SORT ON THE VALUE, NOT THE LABEL. The table shows DD/MM/YYYY, and
 *    sorting those strings puts 01/02/2026 before 30/01/2026. We sort on the raw ISO
 *    timestamp the API sent.
 *
 * 3. EMPTY IS NOT SMALL. "Never logged in" and "no department" are absences, not low
 *    values. They sort LAST in both directions — flipping the arrow to find your
 *    longest-dormant employee shouldn't fill the top of the list with people who have
 *    never logged in at all.
 */

export const REACH_LABELS = {
  all: 'Company-wide',
  specific: 'Selected departments',
  own: 'Own department',
};

/** Rank for role, so sorting reads as seniority rather than alphabetically —
 *  plain A-Z gives admin, employee, manager, which looks like a bug. */
const ROLE_RANK = { superadmin: 0, admin: 1, manager: 2, employee: 3 };

/** Company-wide first: the widest reach is the one worth finding. */
const REACH_RANK = { all: 0, specific: 1, own: 2 };

/** Only a manager has reach. Admins see everything by role, employees see themselves,
 *  so for both the honest answer is "not applicable" rather than "own department". */
export function reachOf(emp) {
  if (!emp || emp.role !== 'manager') return null;
  const view = emp.manager_view_scope || 'own';
  return REACH_LABELS[view] ? view : 'own';
}

export function reachLabel(emp) {
  const key = reachOf(emp);
  return key ? REACH_LABELS[key] : null;
}

const text = (v) => (v === null || v === undefined || v === '' ? null : String(v).toLowerCase());
const time = (v) => {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};

/**
 * The comparable value for one row and column. `null` always means "absent" and is
 * pushed to the bottom by `compareEmployees`, never treated as zero.
 *
 * `breakdown` is the row's leave figures, passed in rather than recomputed: the page
 * already derives them once per row (`leaveBreakdownFor`), and a second derivation here
 * would be one more place for the numbers to disagree.
 */
export function sortValue(emp, key, breakdown) {
  switch (key) {
    case 'name':
      return text(`${emp.first_name || ''} ${emp.last_name || ''}`.trim());
    case 'department':
      return text(emp.department_name);
    case 'location':
      return text(emp.location_name);
    case 'role':
      return ROLE_RANK[emp.role] ?? 99;
    case 'reach': {
      const r = reachOf(emp);
      return r === null ? null : REACH_RANK[r];
    }
    case 'balance':
      return {
        minutes: breakdown ? breakdown.discretionaryMinutes : null,
        days: breakdown ? breakdown.discretionary : null,
      };
    // Sorted by HOURS, not by days per week. Two people can both work three days
    // and one of them do mornings only — hours is the honest ordering, and it is
    // the number the payroll report leans on. Falls back to days when a pattern
    // gives no hours at all.
    case 'workingWeek':
      return emp.weekly_hours ?? emp.working_days_per_week ?? null;
    case 'bankHols':
      return emp.works_public_holidays ? null : (breakdown ? breakdown.bhRem : null);
    case 'totalRemaining':
      return {
        minutes: breakdown ? breakdown.totalRemainingMinutes : null,
        days: breakdown ? breakdown.totalRemaining : null,
      };
    case 'invited':
      return time(emp.invited_at);
    case 'lastLogin':
      return time(emp.last_login_at);
    default:
      return null;
  }
}

/** Compare two balance shapes: minutes if BOTH sides have them, else days. */
function compareBalance(a, b) {
  const bothMinutes = a.minutes !== null && a.minutes !== undefined
    && b.minutes !== null && b.minutes !== undefined;
  const av = bothMinutes ? a.minutes : a.days;
  const bv = bothMinutes ? b.minutes : b.days;
  if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
  if (bv === null || bv === undefined) return -1;
  return av === bv ? 0 : (av < bv ? -1 : 1);
}

export function compareEmployees(a, b, key, dir, breakdownFor) {
  const bf = breakdownFor || (() => null);
  const av = sortValue(a, key, bf(a));
  const bv = sortValue(b, key, bf(b));
  const sign = dir === 'desc' ? -1 : 1;

  if (av && typeof av === 'object') {
    // Balances carry their own absent-handling inside compareBalance, but an absent
    // balance must still sink in BOTH directions, so it is not multiplied by sign.
    const aMissing = (av.minutes === null || av.minutes === undefined)
      && (av.days === null || av.days === undefined);
    const bMissing = (bv.minutes === null || bv.minutes === undefined)
      && (bv.days === null || bv.days === undefined);
    if (aMissing || bMissing) return aMissing && bMissing ? 0 : (aMissing ? 1 : -1);
    return compareBalance(av, bv) * sign;
  }

  // Absent sinks in both directions — see rule 3 in the module docstring.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  if (av === bv) return 0;
  return (av < bv ? -1 : 1) * sign;
}

/** Stable sort (Array.prototype.sort is stable in every browser we support), so rows
 *  that tie keep the order they already had rather than shuffling on each re-render. */
export function sortEmployees(list, key, dir, breakdownFor) {
  if (!key) return list;
  return [...list].sort((a, b) => compareEmployees(a, b, key, dir, breakdownFor));
}

/**
 * `status` is part of the filter set, not a separate toggle beside it.
 *
 * It used to be a "Show leavers" checkbox that ADDED the archived people into the list
 * greyed out — which is the opposite of what anyone ticking it wants. You tick it
 * because you're looking FOR a leaver (to restore or delete one), and mixing two of
 * them into thirty-four active people means hunting for the thing you just asked to
 * see. Rob's call, 01/08/2026: make it a filter like the other five, with an explicit
 * "Everyone" for the combined view rather than it being the only option.
 *
 * 'active' is the default, so anything else counts as a filter for the "Clear filters"
 * badge — a list quietly showing only leavers must announce itself like any other
 * narrowed view.
 */
export const STATUS = { ACTIVE: 'active', LEAVERS: 'leavers', ALL: 'all' };

export const EMPTY_FILTERS = {
  search: '',
  department_id: '',
  location_id: '',
  role: '',
  tag_id: '',
  reach: '',
  status: STATUS.ACTIVE,
};

export function activeFilterCount(f) {
  const x = { ...EMPTY_FILTERS, ...(f || {}) };
  return ['department_id', 'location_id', 'role', 'tag_id', 'reach']
    .filter((k) => x[k]).length
    + (x.search ? 1 : 0)
    + (x.status !== STATUS.ACTIVE ? 1 : 0);
}

/**
 * One row against the filter bar. Every filter is AND-ed; an empty filter matches
 * everything. Kept here rather than inline in the page so "why is this person hidden?"
 * has exactly one answer to read.
 */
export function matchesFilters(emp, f) {
  const x = { ...EMPTY_FILTERS, ...(f || {}) };

  // Leavers are archived people (lifecycle Phase A). Deleted people never reach the
  // client at all, so there is no third state to worry about here.
  const archived = !!emp.is_archived;
  if (x.status === STATUS.ACTIVE && archived) return false;
  if (x.status === STATUS.LEAVERS && !archived) return false;

  if (x.search) {
    const hay = `${emp.first_name || ''} ${emp.last_name || ''} ${emp.email || ''}`.toLowerCase();
    if (!hay.includes(x.search.toLowerCase())) return false;
  }
  if (x.department_id && String(emp.department_id || '') !== String(x.department_id)) return false;
  if (x.location_id && String(emp.location_id || '') !== String(x.location_id)) return false;
  if (x.role && emp.role !== x.role) return false;
  if (x.tag_id && !(emp.tags || []).some((t) => String(t.id) === String(x.tag_id))) return false;
  // Reach only ever matches managers, so filtering by it implicitly filters to them.
  if (x.reach && reachOf(emp) !== x.reach) return false;
  return true;
}
